// src/components/Sidebar.jsx
import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { coercePlainText } from "./UI";
import BrandLogo from "./BrandLogo";

const iconMap = {
  "◈": "◈", "◉": "◉", "◇": "◇", "◆": "◆", "◎": "◎", "▨": "▨", "⬡": "⬡",
};

function NavItem({ to, icon, label, onNavigate }) {
  return (
    <NavLink to={to} style={({ isActive }) => ({
      display: "flex", alignItems: "center", gap: 10,
      padding: "10px 14px", borderRadius: 4, cursor: "pointer",
      transition: "all 0.15s", textDecoration: "none",
      color: isActive ? "var(--gold-light)" : "rgba(245,240,232,0.55)",
      background: isActive ? "rgba(184,150,90,0.12)" : "transparent",
      fontSize: 13, fontWeight: 300, letterSpacing: "0.03em", marginBottom: 1,
    })} onClick={onNavigate}>
      <span style={{ fontSize: 15, width: 20, textAlign: "center" }}>{icon}</span>
      {label}
    </NavLink>
  );
}

export default function Sidebar({ isMobile = false, open = false, onClose }) {
  const { profile, role, logout } = useAuth();
  const navigate = useNavigate();

  const adminNav = [
    { to: "/admin", icon: "◈", label: "Overview" },
    { to: "/admin/designers", icon: "◉", label: "Designers" },
    { to: "/admin/contractors", icon: "▣", label: "Contractors" },
    { to: "/admin/orders", icon: "◇", label: "Orders" },
    { to: "/admin/commissions", icon: "◆", label: "Commissions" },
    { to: "/admin/referrals", icon: "⬡", label: "Referrals" },
    { to: "/admin/program", icon: "◎", label: "Program Settings" },
    { to: "/admin/reports", icon: "▨", label: "Reports" },
  ];

  const designerNav = [
    { to: "/portal", icon: "◈", label: "My Dashboard" },
    { to: "/portal/log-order", icon: "▣", label: "Log a sale" },
    { to: "/portal/orders", icon: "◇", label: "My Orders" },
    { to: "/portal/earnings", icon: "◆", label: "My Earnings" },
    { to: "/portal/referral", icon: "⬡", label: "Client referral" },
    { to: "/portal/my-referrals", icon: "◐", label: "My referrals" },
    { to: "/portal/invite-designer", icon: "✦", label: "Invite a designer" },
    { to: "/portal/program", icon: "◎", label: "Program Info" },
  ];

  const nav = role === "admin" ? adminNav : designerNav; // contractors share designerNav routes

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <aside style={{
      background: "var(--taupe)", width: 240, flexShrink: 0,
      display: "flex", flexDirection: "column",
      height: "100vh",
      position: isMobile ? "fixed" : "sticky",
      top: 0,
      left: 0,
      overflowY: "auto",
      zIndex: 200,
      transform: isMobile ? (open ? "translateX(0)" : "translateX(-100%)") : "none",
      transition: isMobile ? "transform 0.2s ease" : "none",
      boxShadow: isMobile && open ? "var(--shadow)" : "none",
    }}>
      <div style={{ padding: "26px 22px 20px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
        {isMobile && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            style={{
              position: "absolute",
              top: 10,
              right: 10,
              border: "none",
              background: "transparent",
              color: "var(--cream)",
              fontSize: 20,
            }}
          >
            ×
          </button>
        )}
        <BrandLogo variant="light" style={{ maxWidth: 168 }} />
        <div style={{ fontSize: 9, fontWeight: 500, letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(255,255,255,0.45)", marginTop: 12 }}>
          {role === "contractor" ? "Contractor Program" : role === "admin" ? "Admin" : "Designer Program"}
        </div>
      </div>

      {/* User */}
      <div style={{ padding: "18px 28px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ display: "inline-block", fontSize: 9, fontWeight: 500, letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--gold)", background: "rgba(184,150,90,0.12)", padding: "3px 8px", borderRadius: 2, marginBottom: 6 }}>
          {role === "admin" ? "Admin" : `${role === "contractor" ? "Contractor" : "Designer"} · ${coercePlainText(profile?.tier)}`}
        </div>
        <div style={{ fontSize: 14, color: "var(--cream)", fontWeight: 400 }}>{profile?.name || "—"}</div>
        <div style={{ fontSize: 11, color: "var(--light)", marginTop: 2 }}>{profile?.email || ""}</div>
      </div>

      {/* Nav */}
      <nav style={{ padding: "18px 14px", flex: 1 }}>
        {nav.map(item => <NavItem key={item.to} {...item} onNavigate={isMobile ? onClose : undefined} />)}
      </nav>

      {/* Logout */}
      <div style={{ padding: "18px 28px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
        <button onClick={handleLogout} style={{ background: "none", border: "none", color: "rgba(245,240,232,0.4)", fontFamily: "'Jost',sans-serif", fontSize: 12, cursor: "pointer", letterSpacing: "0.05em", transition: "color 0.2s" }}
          onMouseEnter={e => e.target.style.color = "var(--cream)"}
          onMouseLeave={e => e.target.style.color = "rgba(245,240,232,0.4)"}>
          ← Sign Out
        </button>
      </div>
    </aside>
  );
}
